import { reactive, ref, watch } from 'vue'
import { setData } from './data'
import { DriverSocketMsgType, DriverSocketMsg } from '@libs/types/message'
import { DriverService } from './driver'
import { operate } from './operate'
import { MacroParam } from '@libs/types/response'
import { SpecialKeys, mediaKeys, mouseBaseKeys } from '@/define/vkcode'
import { VK } from '@/define/define'
import { normalizeKeyCode } from '@/tool/utility'
import { DeviceType, MagnetSeniorType } from '@libs/types/enum'
import { SetMagnetSenior } from '../response'
import { enableDialog } from '@/components/cdialog/dialog'
import { speciaBehiver, specialCheckCurrent } from '@/define/limit'
import { getCurrentDevice } from '@/profiles/selector'
import { Fe87v2DeviceList } from '@/define/device_vendor'
import { DeviceManager } from '../device'

import { ActionType, BaseTabs, ListenType, SetType, TriggerType } from './action_types'

export { ActionType, BaseTabs, ListenType, SetType, TriggerType }

export interface macro {
  CycleType: number
  CycleTimes: number
  Records: MacroParam[]
}

class ActionSet {
  setType = ref(SetType.routine)
  actionType = ref(ActionType.base)
  fireShow = ref(false)

  macroValue = reactive({
    CycleType: 0,
    CycleTimes: 2,
    Records: [],
  } as macro)

  multimediaValue = ref(0)

  baseKeys = reactive({
    baseTab: BaseTabs.base,
    selectedKeys: [0, 0, 0], // 前两位辅助键，后一位是普通按键
  })

  magnetValue = reactive({
    KeyType: MagnetSeniorType.SeniorKeyTypeNone,
    Keys: [],
    MTDuration: 50, // 默认 500ms
    TGLMode: TriggerType.continuous,
    KMEMode: [0, 0],
    DelayTime: 0,
  } as SetMagnetSenior)

  // 模式特定时间（UI层管理，单位统一为 ms）
  modeTimes = reactive({
    [SetType.mt]: 500,
    [SetType.kme]: 0,
    [SetType.rs]: 0,
    [SetType.end]: 0,
  })

  listenKey = reactive([] as MacroParam[])

  isSupportCompose(type: SetType = this.setType.value) {
    if (type === SetType.kme || type === SetType.macro) return false
    return !specialCheckCurrent(speciaBehiver.NotSupportModifier)
  }

  isSupportMouse(type: SetType = this.setType.value) {
    if (getCurrentDevice.value.deviceType == DeviceType.mouse) return true
    const isMouseTabDevice =
      specialCheckCurrent(speciaBehiver.DevicesOfZhihuiwuxian) ||
      specialCheckCurrent(speciaBehiver.DevicesOfHuoChaiRen) ||
      specialCheckCurrent(speciaBehiver.DevicesOfJingtai_V1) ||
      specialCheckCurrent(speciaBehiver.DevicesOfJingtai_V2) ||
      Fe87v2DeviceList.includes(DeviceManager.instance.state.currentPid)

    if (isMouseTabDevice) {
      return type == SetType.routine
    }
    if (specialCheckCurrent(speciaBehiver.DeviceOfRoyuan)) {
      return type != SetType.kme
    }
    return false
  }

  isSupportMultimedia(type: SetType = this.setType.value) {
    return type == SetType.routine
  }

  isSupportFire(type: SetType = this.setType.value) {
    if (type === SetType.kme) return false
    return specialCheckCurrent(speciaBehiver.SupportFireKey)
  }

  constructor() {
    this.watchCurrentKey()
    watch(this.setType, (val) => {
      if (operate.currentKey.value >= 0) {
        this.initBaseKeys()
        this.initMagenetData()
        this.initMacroData()
        this.initMultimediaValue()
      } else {
        /* 未选中按键时的预设(Preset)模式逻辑：确保用户在切换模式准备设置时，UI 状态符合模式限制 */
        if (!this.isSupportCompose(val) && this.baseKeys.baseTab === BaseTabs.compose) {
          this.baseKeys.baseTab = BaseTabs.base
        }
        if (!this.isSupportMouse(val) && this.baseKeys.baseTab === BaseTabs.mouse) {
          this.baseKeys.baseTab = BaseTabs.base
        }
        if (!this.isSupportFire(val)) {
          this.fireShow.value = false
        }
        this.initBaseKeysBySwitchInput(this.baseKeys.selectedKeys)
      }
    })
  }

  private getTypeForMagnet(magnetType: MagnetSeniorType): SetType {
    const map: any = {
      [MagnetSeniorType.SeniorKeyTypeDKS]: SetType.dks,
      [MagnetSeniorType.SeniorKeyTypeMT]: SetType.mt,
      [MagnetSeniorType.SeniorKeyTypeTGL]: SetType.tgl,
      [MagnetSeniorType.SeniorKeyTypeKME]: SetType.kme,
      [MagnetSeniorType.SeniorKeyTypeRS]: SetType.rs,
      [MagnetSeniorType.SeniorKeyTypeMPT]: SetType.mpt,
      [MagnetSeniorType.SeniorKeyTypeEND]: SetType.end,
    }
    return map[magnetType] || SetType.routine
  }

  watchKey() {
    DriverService.instance.setHandler(DriverSocketMsgType.MSWatchKey, this.handleListenKey)
  }

  private watchCurrentKey() {
    watch(operate.currentKey, () => {
      const keyIndex = operate.currentKey.value
      // 如果未选中按键，不主动重置，保留当前 UI 状态以便“先设置后执行”
      if (keyIndex < 0) return

      // 查找该键是否存在已绑定的数据，若有则自动切换类型并加载
      let found = false

      // 1. 检查磁轴高级设置 (DKS, MT, TGL etc.)
      const magnet = setData.getActionData.Magenet && setData.getActionData.Magenet[keyIndex]
      if (magnet && magnet.KeyType !== MagnetSeniorType.SeniorKeyTypeNone) {
        this.setType.value = this.getTypeForMagnet(magnet.KeyType)
        this.initMagenetData()
        found = true
      }

      // 2. 检查宏
      if (!found) {
        const macro = setData.getActionData.Macros && setData.getActionData.Macros[keyIndex]
        if (macro) {
          this.setType.value = SetType.macro
          this.initMacroData()
          found = true
        }
      }

      // 3. 检查常规及多媒体
      if (!found) {
        const keyMap = setData.getActionData.KeyMap && setData.getActionData.KeyMap[keyIndex]
        const keyMapModifier = setData.getActionData.KeyMapModifier && setData.getActionData.KeyMapModifier[keyIndex]
        const keyFire = setData.getActionData.KeyMapFire && setData.getActionData.KeyMapFire[keyIndex]

        if (keyMap || keyMapModifier || keyFire) {
          this.setType.value = SetType.routine
          if (keyMap && mediaKeys().includes(keyMap)) {
            this.actionType.value = ActionType.multiMedia
            this.initMultimediaValue()
          } else {
            this.actionType.value = ActionType.base
            this.initBaseKeys()
          }
          found = true
        }
      }

      // 4. 若该键位为空，则不执行 init 操作，从而保留当前 UI 面板中的临时输入数据（支持“先设置后绑定”流程）
    })
  }

  initBaseKeys() {
    let keyFire = setData.getActionData.KeyMapFire
    let isFire = !!(keyFire && keyFire[operate.currentKey.value])

    const newSelectedKeys: VK[] = [0, 0, 0]
    let keyMap = setData.getActionData.KeyMap
    let found = false
    if (keyMap && keyMap[operate.currentKey.value]) {
      newSelectedKeys[2] = keyMap[operate.currentKey.value]
      found = true
    }

    let KeyMapModifier = setData.getActionData.KeyMapModifier
    if (KeyMapModifier && KeyMapModifier[operate.currentKey.value]) {
      newSelectedKeys[0] = KeyMapModifier[operate.currentKey.value][0]

      if (KeyMapModifier[operate.currentKey.value].length < 3) {
        newSelectedKeys[2] = KeyMapModifier[operate.currentKey.value][1]
      } else {
        newSelectedKeys[1] = KeyMapModifier[operate.currentKey.value][1]
        newSelectedKeys[2] = KeyMapModifier[operate.currentKey.value][2]
      }
      found = true
    }

    // 只有当类型匹配或从 watch(setType) 调用时，才同步基础按键状态到 UI
    this.fireShow.value = isFire
    if (isFire) {
      this.baseKeys.baseTab = BaseTabs.mouse
      this.baseKeys.selectedKeys = [0, 0, 0]
      return
    }
    this.baseKeys.selectedKeys = newSelectedKeys
    if (found) {
      this.determineBaseTab(newSelectedKeys)
    } else {
      this.baseKeys.baseTab = BaseTabs.base
    }
  }

  private determineBaseTab(data: VK[]) {
    if (!this.isSupportCompose() && (data[0] > 0 || data[1] > 0)) {
      this.baseKeys.baseTab = BaseTabs.base
      return
    }

    if (data[0] > 0 || data[1] > 0) {
      this.baseKeys.baseTab = BaseTabs.compose
    } else {
      if (this.isSupportMouse() && mouseBaseKeys().includes(data[2])) {
        this.baseKeys.baseTab = BaseTabs.mouse
      } else if (SpecialKeys().includes(data[2])) {
        this.baseKeys.baseTab = BaseTabs.extend
      } else {
        this.baseKeys.baseTab = BaseTabs.base
      }
    }
  }

  initMultimediaValue() {
    let data = setData.getActionData.KeyMap[operate.currentKey.value]
    if (data && mediaKeys().includes(data)) {
      this.multimediaValue.value = data
    } else {
      this.multimediaValue.value = 0
    }
  }

  initMagenetData() {
    let magnet = setData.getActionData.Magenet
    if (magnet && magnet[operate.currentKey.value]) {
      let data = JSON.parse(JSON.stringify(magnet[operate.currentKey.value]))
      this.magnetValue.KeyType = data.KeyType
      this.magnetValue.Keys = data.Keys
      this.magnetValue.MTDuration = data.MTDuration
      this.magnetValue.KMEMode = data.KMEMode
      this.magnetValue.TGLMode = data.TGLMode
      this.magnetValue.DelayTime = data.DelayTime == undefined ? 0 : data.DelayTime

      // 同步到模式特定时间（UI展示用）
      this.modeTimes[SetType.mt] = (data.MTDuration || 0) * 10
      this.modeTimes[SetType.kme] = data.DelayTime || 0
      this.modeTimes[SetType.rs] = data.DelayTime || 0
      this.modeTimes[SetType.end] = data.DelayTime || 0

      /* 针对当前模式的兼容性清洗逻辑 */
      this.magnetValue.Keys.forEach((k) => {
        if (k.KeyCode) {
          if (!this.isSupportCompose()) {
            k.KeyCode = normalizeKeyCode(k.KeyCode, BaseTabs.base)
          }
          if (!this.isSupportMouse() && mouseBaseKeys().includes(k.KeyCode[2])) {
            k.KeyCode[2] = 0
          }
        }
      })

      // 仅当处于高级模式时，才同步当前选中的按键到基础按键区（键盘高亮）
      if (this.setType.value != SetType.routine && this.setType.value != SetType.macro) {
        // let focusKeys: VK[] = [0, 0, 0]
        // if (data.KeyType == MagnetSeniorType.SeniorKeyTypeMT) {
        //   focusKeys = data.Keys[1]?.KeyCode || [0, 0, 0]
        // } else if (data.Keys && data.Keys.length > 0) {
        //   focusKeys = data.Keys[0]?.KeyCode || [0, 0, 0]
        // }
        // this.initBaseKeysBySwitchInput(focusKeys)
      }
    } else {
      this.magnetValue.KeyType = MagnetSeniorType.SeniorKeyTypeNone
      this.magnetValue.Keys = this.defaultMagnetKeys
      this.magnetValue.MTDuration = 0
      this.magnetValue.KMEMode = [0, 0]
      this.magnetValue.TGLMode = TriggerType.continuous
      this.magnetValue.DelayTime = 0
    }
  }

  changeSelectedKeys(data: VK[]) {
    this.baseKeys.selectedKeys = [...data]
  }

  changeBaseTab(data: BaseTabs) {
    if (this.baseKeys.baseTab !== data) {
      this.baseKeys.baseTab = data
      this.baseKeys.selectedKeys = [0, 0, 0]
    }
  }

  changeActionType(t: ActionType) {
    if (this.actionType.value !== t) {
      this.actionType.value = t
      this.baseKeys.baseTab = BaseTabs.base
      this.baseKeys.selectedKeys = [0, 0, 0]
      this.fireShow.value = false
    }
  }

  initBaseKeysBySwitchInput(data: VK[]) {
    let keys = normalizeKeyCode(JSON.parse(JSON.stringify(data)), BaseTabs.base)
    if (data.filter((i) => i > 0).length > 1) {
      if (!this.isSupportCompose()) {
        keys = normalizeKeyCode(JSON.parse(JSON.stringify(data)), BaseTabs.base)
      } else {
        keys = normalizeKeyCode(JSON.parse(JSON.stringify(data)), BaseTabs.compose)
      }
    }
    // 兼容性清洗鼠标键
    if (!this.isSupportMouse() && mouseBaseKeys().includes(keys[2])) {
      keys[2] = 0
    }

    /* 同步修正 magnetValue 中的预设 Keys，防止“预设模式 -> 切换不支持的模式 -> 绑定”流程中带入非法数据 */
    if (!this.isSupportCompose() || !this.isSupportMouse()) {
      if (this.magnetValue.Keys.length > 0) {
        this.magnetValue.Keys[0].KeyCode = keys
      }
    }

    this.changeSelectedKeys(keys)
    actionSet.determineBaseTab(keys)
  }

  clearListenKey() {
    this.listenKey.length = 0
  }

  initMacroData() {
    this.listenKey.length = 0
    let data = setData.getActionData.Macros[operate.currentKey.value]
    let type = setData.getActionData.MacroType[operate.currentKey.value]
    if (data && type) {
      this.macroValue.CycleTimes = type.LoopTimes || 2
      this.macroValue.CycleType = type.LoopType
      let records = JSON.parse(JSON.stringify(data))
      this.macroValue.Records = records
      this.listenKey.push(...records)
    } else {
      this.macroValue.CycleType = 0
      this.macroValue.CycleTimes = 2
      this.macroValue.Records = []
    }
  }

  private handleListenKey(data: MacroParam) {
    const macroLength = Math.floor(DeviceManager.instance.state.currentDevice.Limit.MacroBytesLen / 4)
    if (actionSet.listenKey.length < macroLength) {
      actionSet.listenKey.push(data)
    }
  }

  changeSetType(t: SetType) {
    this.setType.value = t
  }

  listenkey(t: ListenType): Promise<MacroParam[]> {
    const data = {
      Type: DriverSocketMsgType.MTWatchKey,
      Data: {
        WatchType: t,
      },
    } as DriverSocketMsg<any>
    return DriverService.instance.askTypeAndData<MacroParam[]>(data)
  }

  resetData() {
    this.setType.value = SetType.routine
    this.actionType.value = ActionType.base
    this.multimediaValue.value = 0
    this.baseKeys.baseTab = BaseTabs.base
    this.baseKeys.selectedKeys = [0, 0, 0]
    this.fireShow.value = false

    this.macroValue.CycleType = 0
    this.macroValue.CycleTimes = 2
    this.macroValue.Records = []
    this.listenKey.length = 0

    this.magnetValue.KeyType = MagnetSeniorType.SeniorKeyTypeNone
    this.magnetValue.Keys = this.defaultMagnetKeys
    this.magnetValue.MTDuration = 50
    this.magnetValue.KMEMode = [0, 0]
    this.magnetValue.TGLMode = TriggerType.continuous
    this.magnetValue.DelayTime = 0

    this.modeTimes[SetType.mt] = 500
    this.modeTimes[SetType.kme] = 0
    this.modeTimes[SetType.rs] = 0
    this.modeTimes[SetType.end] = 0
  }

  async resetBaseValue() {
    if (operate.currentKey.value < 0) {
      return
    }
    const res = await setData.setKeyMap(operate.currentKey.value, 0, []).catch((e) => {
      enableDialog(e)
    })
    if (this.magnetValue.KeyType == MagnetSeniorType.SeniorKeyTypeKME) {
      setData.askData()
    }
    this.resetData()
    if (operate.currentKey.value < 0) {
      return
    }
    return res
  }

  // 保存火力键后，清除其他数据
  saveFireKeyAndClearOthers() {
    this.macroValue.Records = []
    this.multimediaValue.value = 0
    this.baseKeys.selectedKeys = [0, 0, 0]
  }

  saveMacro() {
    if (this.macroValue.Records.length == 0) {
      return
    }
    this.multimediaValue.value = 0
    this.actionType.value = ActionType.base
    this.baseKeys.selectedKeys = [0, 0, 0]

    if (operate.currentKey.value < 0) {
      return
    }
    if (this.macroValue.CycleTimes < 1) {
      this.macroValue.CycleTimes = 1
    }
    return setData.saveKeyMacro(operate.currentKey.value, JSON.parse(JSON.stringify(this.macroValue)))
  }

  get defaultMagnetKeys() {
    const arr = []
    arr.push(
      {
        KeyCode: [0, 0, 0],
        KeyFunction: [0, 0, 0, 0],
        Travel: 0,
      },
      {
        KeyCode: [0, 0, 0],
        KeyFunction: [0, 0, 0, 0],
        Travel: 0,
      },
      {
        KeyCode: [0, 0, 0],
        KeyFunction: [0, 0, 0, 0],
        Travel: 0,
      },
      {
        KeyCode: [0, 0, 0],
        KeyFunction: [0, 0, 0, 0],
        Travel: 0,
      },
    )
    return arr
  }
}
let actionSet = new ActionSet()
export { actionSet }
